import React, { ComponentProps, useMemo } from "react";
import { Image } from "react-bootstrap";
import { StreakStatus } from "@nestcoinco/hyper-api-gateway-api-client";

import "./StreakDays.scss";
import ImageCheckmark from "../../../images/checkmark-primary.svg";
import ImageTrophyStar from "../../../images/trophy-star.svg";

export enum DayStatus {
  COMPLETED = "completed",
  ACTIVE = "active",
  PENDING = "pending",
  OFF_STREAK = "off-streak",
}

export interface IStreakDay {
  name: string;
  date: string;
  status: DayStatus;
  isLastDay?: boolean;
}

const DAY_NAMES: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] = [
  "Su",
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
];

export function generateStreakDays(
  streakCount: number,
  isTodayCompleted: boolean
): IStreakDay[] {
  const streakDays: IStreakDay[] = [];
  const today = new Date();
  const todayDate = today.getDate();
  const maxStreakLength = 15;
  let count: number;

  if (streakCount === 0) count = 0;
  else if (isTodayCompleted) count = streakCount - 1;
  else count = streakCount;

  // Calculate starting point to ensure at least 2 completed days when possible
  const startDayOffset = Math.max(-2, -count);

  for (let i = startDayOffset; i < startDayOffset + maxStreakLength; i++) {
    const currentDate = new Date(today);
    currentDate.setDate(todayDate + i);

    const dayIndex = currentDate.getDay();
    const dateString = currentDate.toISOString().split("T")[0];

    let status: DayStatus;
    let isLastDay = false;

    if (i < 0 || (i === 0 && isTodayCompleted)) {
      status = DayStatus.COMPLETED;
    } else if (i === 0) {
      status = DayStatus.ACTIVE;
    } else if (i > 0 && i < maxStreakLength - count) {
      status = DayStatus.PENDING;
    } else {
      status = DayStatus.OFF_STREAK;
    }

    if (i === maxStreakLength - 1 - count) isLastDay = true;

    streakDays.push({
      name: DAY_NAMES[dayIndex],
      date: dateString,
      status: status,
      isLastDay: isLastDay,
    });
  }

  streakDays.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return streakDays;
}

interface StreakDayProps {
  status: DayStatus;
  name: string;
  date: string;
  isLastDay?: boolean;
}

function StreakDay({ status, name, date, isLastDay }: StreakDayProps) {
  return (
    <div
      className="streak-day d-flex flex-column justify-content-center align-items-center gap-2"
      data-status={status}
    >
      {isLastDay ? (
        <Image src={ImageTrophyStar} alt="trophy" />
      ) : (
        <p data-status={status}>{name}</p>
      )}
      {(status === DayStatus.PENDING || status === DayStatus.OFF_STREAK) && (
        <p>{date.split("-")[2]}</p>
      )}
      {status === DayStatus.ACTIVE && <span className="circle" />}
      {status === DayStatus.COMPLETED && (
        <Image src={ImageCheckmark} alt="checkmark" />
      )}
    </div>
  );
}

interface StreakDaysProps extends ComponentProps<"div"> {
  status: StreakStatus;
}

function StreakDays({ status, className = "", ...restProps }: StreakDaysProps) {
  const streakDays = useMemo(
    () => generateStreakDays(status.streakCount, status.todayCompleted),
    [status.streakCount, status.todayCompleted]
  );

  const screenOneStreakDays = useMemo(
    () => streakDays.slice(0, 7),
    [streakDays]
  );
  const screenTwoStreakDays = useMemo(
    () => streakDays.slice(7, 14),
    [streakDays]
  );
  const screenThreeStreakDays = useMemo(
    () => streakDays.slice(14),
    [streakDays]
  );

  return (
    <div
      className={`streak-days-container scrollbar-hidden d-flex justify-content-between w-100 ${className}`}
      {...restProps}
    >
      <div className="screen-wrapper">
        {screenOneStreakDays.map((day) => (
          <StreakDay key={day.date} {...day} />
        ))}
      </div>
      <div className="screen-wrapper">
        {screenTwoStreakDays.map((day) => (
          <StreakDay key={day.date} {...day} />
        ))}
      </div>
      <div className="d-flex align-items-center">
        {screenThreeStreakDays.map((day) => (
          <StreakDay key={day.date} {...day} />
        ))}
      </div>
    </div>
  );
}

export default StreakDays;
